// src/components/CartDrawer.js
import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { LanguageContext } from "../context/LanguageContext";
import { apiRequest } from "../services/api";

function CartDrawer() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();
  const {
    isDrawerOpen,
    closeDrawer,
    cartItems,
    subtotal,
    discount,
    total,
  } = useCart();

  const t = {
    EN: {
      title: "YOUR CART",
      viewCart: "VIEW FULL CART",
      checkout: "CHECKOUT",
      empty: "Your cart is empty.",
    },
    AR: {
      title: "سلة المشتريات",
      viewCart: "عرض السلة كاملة",
      checkout: "إتمام الشراء",
      empty: "سلتك فارغة.",
    },
  }[lang];

  const handleViewCart = () => {
    apiRequest("/analytics/track", {
      method: "POST",
      body: JSON.stringify({
        action_type: "open_cart_page",
        entity_type: "cart",
      }),
    }).catch(() => {});

    closeDrawer();
    navigate("/cart");
  };

  return (
    <div
      className={`
        fixed inset-0 z-[9999]
        transition-opacity duration-300
        ${
          isDrawerOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }
      `}
    >
      {/* BACKDROP */}
      <div
        className="absolute inset-0 bg-black/70"
        onClick={closeDrawer}
      />

      {/* PANEL */}
      <div
        className={`
          absolute top-0 right-0 h-full 
          w-[82%] sm:w-[380px] md:w-[420px]
          bg-black text-white
          border-l border-[rgba(46,150,42,0.35)]
          shadow-2xl
          transform transition-transform duration-300
          ${isDrawerOpen ? "translate-x-0" : "translate-x-full"}
        `}
        dir={lang === "AR" ? "rtl" : "ltr"}
      >
        {/* HEADER */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[rgba(46,150,42,0.35)]">
          <h3 className="text-sm font-semibold tracking-widest text-[#2E5A2A]">
            {t.title}
          </h3>
          <button
            onClick={closeDrawer}
            className="text-2xl opacity-60 hover:opacity-100 transition"
          >
            ×
          </button>
        </div>

        {/* CONTENT */}
        <div className="flex flex-col h-[calc(100%-60px)]">
          {/* ITEMS */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {cartItems.length === 0 && (
              <p className="text-sm text-gray-400 mt-10 text-center">
                {t.empty}
              </p>
            )}

            {cartItems.map((item) => (
              <div
                key={item.id}
                className="flex gap-3 border-b border-[rgba(255,255,255,0.08)] pb-3"
              >
                <img
                  src={item.image}
                  alt={lang === "EN" ? item.nameEN : item.nameAR}
                  className="w-16 h-16 object-cover rounded-md border border-[rgba(46,150,42,0.35)]"
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold">
                    {lang === "EN" ? item.nameEN : item.nameAR}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Qty: {item.qty}
                  </p>
                  <p className="text-sm font-bold text-[#2E5A2A] mt-1">
                    ${item.price}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* SUMMARY + ACTIONS */}
          {cartItems.length > 0 && (
            <div className="border-t border-[rgba(46,150,42,0.35)] px-5 py-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-300">Subtotal</span>
                <span className="font-semibold">${subtotal}</span>
              </div>

              {discount > 0 && (
                <div className="flex justify-between text-xs text-gray-400">
                  <span>Discount</span>
                  <span>- ${discount}</span>
                </div>
              )}

              <div className="flex justify-between text-sm font-semibold mt-1">
                <span>Total</span>
                <span className="text-[#2E5A2A]">${total}</span>
              </div>

              <button
                onClick={handleViewCart}
                className="
                  w-full mt-3 py-2.5
                  border border-[#2E5A2A]
                  text-[#2E5A2A]
                  text-sm font-semibold rounded-md
                  hover:bg-[#2E5A2A] hover:text-white
                  transition
                "
              >
                {t.viewCart}
              </button>

              <button
                onClick={() => {
  apiRequest("/analytics/track", {
    method: "POST",
    body: JSON.stringify({
      action_type: "enter_checkout",
      entity_type: "cart",
    }),
  }).catch(() => {});

  // 🔥 META INITIATE CHECKOUT (Drawer)
  if (typeof window.fbq === "function") {
    window.fbq("track", "InitiateCheckout", {
      content_ids: cartItems.map(
        (it) => it.public_id || it.product_id
      ),
      content_type: "product",
      value: Number(total) || 0,
      currency: "USD",
      num_items: cartItems.reduce(
        (s, it) => s + Number(it.qty || 0),
        0
      ),
    });
  }

  closeDrawer();
  navigate("/checkout");
}}

                className="
                  w-full mt-2 py-2.5
                  bg-[#2E5A2A]
                  text-white text-sm font-semibold
                  rounded-md
                  hover:brightness-110
                  transition
                "
              >
                {t.checkout}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CartDrawer;
